import angular from 'angular';
import csmCommonWeb from 'csm-common-web';
import addChangeEntityDialog from '../../dialogs/entities/addChangeEntityDialog/addChangeEntityDialog';
import addChangeStudentDialog from '../../dialogs/entities/addChangeStudentDialog/addChangeStudentDialog';
import addChangeVendorDialog from '../../dialogs/entities/addChangeVendorDialog/addChangeVendorDialog';
import selectEntityTypeTemplate from './selectEntityType.template.html';

export default angular
  .module('books.util.entityMaker', [
    csmCommonWeb,
    addChangeEntityDialog,
    addChangeStudentDialog,
    addChangeVendorDialog
  ])
  .service('EntityMaker', EntityMakerService).name;

/*******************************************************************************
 * Service: EntityMaker
 *
 * Utility to create new entities records in the database. It will utilize the
 * AddChangeEntityDialog, AddChangeStudentDialog, and AddChangeVendorDialog
 * services.
 *
 * This service has the following properties:
 *   newEntity - Function to call to begin the new entity process
 *******************************************************************************/
/* @ngInject */
function EntityMakerService(
  Alert,
  $q,
  $timeout,
  $uibModal,
  SERVER,
  AddChangeEntityDialog,
  AddChangeStudentDialog,
  AddChangeVendorDialog
) {
  /*****************************************************************************
   * Class: Processor
   *
   * Instances of this class manage the entity making process using the following
   * methods:
   *   - addEntity  Process the currently set entity type
   *   - confirm    Confirm the addition of a new entity
   *
   * Constructor:
   *
   * @param defer          Current defer object
   * @param name           New entity name to set
   * @param type           New entity type to add. 0/Null prompts for type
   * @param saveService    Function passed in, which ultimately gets called by
   *                       the AddChangeEntityContent upon save.
   * @param excludeVendors Exclude vendor type from selections
   *****************************************************************************/
  function Processor(defer, name, type, saveService, excludeVendors) {
    var mDefer = defer,
      mNameInput = name,
      mType = type || 0,
      mThis = this;

    /***************************************************************************
     * addEntity */
    /**
     * Shows the appropriate dialog based on mType. If the type is 0, then
     * the select entity type dialog will be used.
     ***************************************************************************/
    this.addEntity = function () {
      switch (mType) {
        case 0:
          showSelectEntityType();
          break;
        case SERVER.BooksDBConstants.ET_STUDENT:
          addStudent();
          break;
        case SERVER.BooksDBConstants.ET_VENDOR:
          addVendor();
          break;
        case SERVER.BooksDBConstants.ET_TEACHER:
        case SERVER.BooksDBConstants.ET_PARENT:
        case SERVER.BooksDBConstants.ET_GRANDPARENT:
        case SERVER.BooksDBConstants.ET_COMMUNITY_PARTNER:
        case SERVER.BooksDBConstants.ET_OTHER:
          addOther();
          break;
        default:
          throw new Error('Entity type ' + type + ' not recognized.');
      }
    };

    /***************************************************************************
     * addStudent */
    /**
     * Shows the student panel for adding a new student.
     ***************************************************************************/
    var addStudent = function () {
      AddChangeStudentDialog.show(0, mNameInput).then(
        mDefer.resolve,
        mDefer.reject
      );
    };

    /***************************************************************************
     * addOther */
    /**
     * Shows the entity panel for adding an unspecialized entity.
     ***************************************************************************/
    var addOther = function () {
      AddChangeEntityDialog.show(0, mType, mNameInput, saveService).then(
        mDefer.resolve,
        mDefer.reject
      );
    };

    /***************************************************************************
     * addVendor */
    /**
     * Shows the vendor panel for adding a new vendor.
     * @private
     ***************************************************************************/
    var addVendor = function () {
      AddChangeVendorDialog.show(0, mNameInput).then(
        mDefer.resolve,
        mDefer.reject
      );
    };

    /***************************************************************************
     * confirm */
    /**
     * Shows a dialog to confirm the addition of the unrecognized name.
     ***************************************************************************/
    this.confirm = function () {
      Alert.confirm(
        mNameInput + ' is not recognized.\nDo you want to Add this Name?',
        'Unknown Name'
      ).then(mThis.addEntity, function () {
        $timeout(mDefer.reject);
      });
    };

    /***************************************************************************
     * showSelectEntityTypes */
    /**
     * Shows a dialog to choose what new entity type to add. Exclude the vendor
     * type if requested. Update mType on selection and process.
     ***************************************************************************/
    function showSelectEntityType() {
      $uibModal
        .open({
          backdrop: 'static',
          size: 'sm',
          controller: function ($scope) {
            /** Parent data object to hold the entity type. */
            $scope.data = {};

            /*------------------------------------------------------------*/
            /* There are separate entity restrictions for individuals and */
            /* vendors. Add the entity types based on these restrictions. */
            /*------------------------------------------------------------*/
            $scope.options = [];

            if (!SERVER.appFlags.restrictIndividuals) {
              $scope.options.push({
                label: 'Student',
                value: SERVER.BooksDBConstants.ET_STUDENT
              });
              $scope.options.push({
                label: 'Teacher',
                value: SERVER.BooksDBConstants.ET_TEACHER
              });
              $scope.options.push({
                label: 'Parent',
                value: SERVER.BooksDBConstants.ET_PARENT
              });
            }

            /*-----------------------------------*/
            /* Grandparent and Community Partner */
            /* types are only available to PTA.  */
            /*-----------------------------------*/
            if (SERVER.currentUser.orgType.startsWith('PTA')) {
              $scope.options.push({
                label: 'Grandparent',
                value: SERVER.BooksDBConstants.ET_GRANDPARENT
              });
              $scope.options.push({
                label: 'Community Partner',
                value: SERVER.BooksDBConstants.ET_COMMUNITY_PARTNER
              });
            }

            /*---------------------------------------------*/
            /* The excludeVendors property is passed in to */
            /* the EntityMaker so we need to check both.   */
            /*---------------------------------------------*/
            if (!excludeVendors && !SERVER.appFlags.restrictVendors)
              $scope.options.push({
                label: 'Vendor',
                value: SERVER.BooksDBConstants.ET_VENDOR
              });

            if (!SERVER.appFlags.restrictIndividuals) {
              $scope.options.push({
                label: 'Other',
                value: SERVER.BooksDBConstants.ET_OTHER
              });
            }
            /*********************************************************************
             * confirm */
            /**
             * Ensure an entityType has been selected before closing the dialog.
             *********************************************************************/
            $scope.confirm = function () {
              if (
                ($scope.needsType = isNaN(Number($scope.data.entityType)))
              )
                return;

              $scope.$close($scope.data.entityType);
            };
          },
          template: selectEntityTypeTemplate
        })
        .result.then(function (entityType) {
          mType = entityType;
          mThis.addEntity();
        }, mDefer.reject);
    }
  }

  /*****************************************************************************
   * newEntity */
  /**
   * Start the entity making process. A promise is returned that will be notified
   * when a new entity has been saved or the process has been cancelled. The
   * resolved promise will be provided an object with the following properties:
   *
   *   added      - New entity added
   *   entity     - Entity object saved
   *   entityType - Saved entity type
   *
   * @param  name           New name to add
   * @param  type           Optional new entity type to add.0=Prompt for type
   * @param saveService     Function passed in, which ultimately gets called by
   *                        the AddChangeEntityContent upon save.
   * @param  skipAlert      Skip the confirmation of adding a new entity
   * @param  excludeVendors Exclude vendors from type selection
   *****************************************************************************/
  this.newEntity = function (
    name,
    type,
    saveService,
    skipAlert,
    excludeVendors
  ) {
    var defer = $q.defer();

    var p = new Processor(defer, name, type, saveService, excludeVendors);

    if (skipAlert) p.addEntity();
    else p.confirm();

    return defer.promise;
  };
}
